import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { Icons } from "@/const/icons";
import styles from "./STNewsDetail.module.css";
import clsx from "clsx";

import { STHeroCommon } from "../STHeroCommon";
import { opzuid, EuropeanUnion, ProvinceLimburg } from "@/assets/images";
import { STSharePopover } from "../STSharePopover";
import { defaultImg } from "../../assets/images";

export const STNewsDetail = ({ newsItem }) => {
  const router = useRouter();

  if (!newsItem) return null;

  const parseLongDescription = (desc) => {
    try {
      if (!desc) return null;
      // If it's a string that looks like JSON, parse it
      const parsed = typeof desc === "string" ? JSON.parse(desc) : desc;

      if (Array.isArray(parsed)) {
        return parsed;
      }

      // If it's a single Draft.js object (has blocks), wrap it in a section
      if (parsed && (parsed.blocks || parsed.content?.blocks)) {
        return [
          {
            content: parsed.blocks ? parsed : parsed.content,
            subHeading: parsed.subHeading || null,
          },
        ];
      }

      return null;
    } catch (e) {
      // Not a JSON string, likely plain HTML or text
      return null;
    }
  };

  const descriptionSections = parseLongDescription(newsItem.long_description);

  const renderStyledText = (
    text,
    inlineStyleRanges,
    entityRanges,
    entityMap,
  ) => {
    if (!text) return "";
    if (typeof text !== "string") {
      text = typeof text === "object" ? (text.text || JSON.stringify(text)) : String(text);
    }

    const applyStyles = (content, start) => {
      const activeStyles = (inlineStyleRanges || []).filter(
        (range) => range.offset <= start && range.offset + range.length > start,
      );
      let styled = content;
      activeStyles.forEach((styleObj) => {
        if (styleObj.style === "BOLD")
          styled = <strong key={`b-${start}`}>{styled}</strong>;
        else if (styleObj.style === "ITALIC")
          styled = <em key={`i-${start}`}>{styled}</em>;
        else if (styleObj.style === "UNDERLINE")
          styled = <u key={`u-${start}`}>{styled}</u>;
        else if (styleObj.style === "STRIKETHROUGH")
          styled = <del key={`s-${start}`}>{styled}</del>;
      });

      // Handle Entities (Link)
      const activeEntityRange = (entityRanges || []).find(
        (range) => range.offset <= start && range.offset + range.length > start,
      );
      if (activeEntityRange) {
        const entity = entityMap?.[activeEntityRange.key];
        if (entity?.type === "LINK" || entity?.type === "link") {
          const url = entity.data?.url || entity.data?.href;
          if (url) {
            styled = (
              <Link
                href={url}
                key={`link-${start}`}
                className="text-clr-black text-decoration-underline fw-medium"
                target={url.startsWith("http") ? "_blank" : "_self"}
                rel={url.startsWith("http") ? "noopener noreferrer" : ""}
              >
                {styled}
              </Link>
            );
          }
        }
      }

      return styled;
    };

    if (
      (!inlineStyleRanges || inlineStyleRanges.length === 0) &&
      (!entityRanges || entityRanges.length === 0)
    ) {
      return text.split("\n").map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < text.split("\n").length - 1 && <br />}
        </React.Fragment>
      ));
    }

    let boundaries = [0, text.length];
    inlineStyleRanges?.forEach((r) => {
      boundaries.push(r.offset);
      boundaries.push(r.offset + r.length);
    });
    entityRanges?.forEach((r) => {
      boundaries.push(r.offset);
      boundaries.push(r.offset + r.length);
    });

    let pos = text.indexOf("\n");
    while (pos !== -1) {
      boundaries.push(pos);
      boundaries.push(pos + 1);
      pos = text.indexOf("\n", pos + 1);
    }

    boundaries = [
      ...new Set(boundaries.filter((b) => b >= 0 && b <= text.length)),
    ].sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
      const start = boundaries[i];
      const end = boundaries[i + 1];
      const segment = text.substring(start, end);
      if (segment === "\n") {
        result.push(<br key={`br-${start}`} />);
      } else if (segment.length > 0) {
        result.push(
          <React.Fragment key={start}>
            {applyStyles(segment, start)}
          </React.Fragment>,
        );
      }
    }
    return result;
  };

  const getAlignmentClass = (block) => {
    if (!block.data) return "";
    const alignment = (
      block.data.textAlign ||
      block.data.alignment ||
      block.data["text-align"] ||
      block.data.textAlignment ||
      ""
    ).toLowerCase();

    if (!alignment) return "";
    switch (alignment) {
      case "center":
        return "text-center";
      case "right":
        return "text-end";
      case "justify":
        return "text-justify";
      case "left":
        return "text-start";
      default:
        return "";
    }
  };

  const getAlignmentStyles = (block) => {
    if (!block.data) return {};
    const alignment = (
      block.data.textAlign ||
      block.data.alignment ||
      block.data["text-align"] ||
      block.data.textAlignment ||
      ""
    ).toLowerCase();
    if (alignment === "justify") return { textAlign: "justify" };
    return {};
  };

  const renderBlocks = (blocks, entityMap) => {
    if (!blocks) return null;
    const elements = [];
    let currentList = [];

    blocks.forEach((block, index) => {
      const styledContent = renderStyledText(
        block.text,
        block.inlineStyleRanges,
        block.entityRanges,
        entityMap,
      );

      const isList = (type) =>
        type === "unordered-list-item" || type === "ordered-list-item";

      if (isList(block.type)) {
        currentList.push({ ...block, styledContent });

        const nextBlock = blocks[index + 1];
        if (!nextBlock || !isList(nextBlock.type)) {
          const renderNestedList = (listBlocks) => {
            let i = 0;

            const processLevel = (currentDepth) => {
              const levelElements = [];

              while (
                i < listBlocks.length &&
                listBlocks[i].depth >= currentDepth
              ) {
                if (listBlocks[i].depth > currentDepth) {
                  const nested = processLevel(listBlocks[i].depth);
                  if (levelElements.length > 0) {
                    const lastIdx = levelElements.length - 1;
                    const lastItem = levelElements[lastIdx];
                    levelElements[lastIdx] = React.cloneElement(lastItem, {
                      children: [lastItem.props.children, nested],
                    });
                  } else {
                    levelElements.push(nested);
                  }
                  continue;
                }

                const blockAtLevel = listBlocks[i];
                const type = blockAtLevel.type;
                const ListTag = type === "ordered-list-item" ? "ol" : "ul";

                const listItems = [];
                while (
                  i < listBlocks.length &&
                  listBlocks[i].depth === currentDepth &&
                  listBlocks[i].type === type
                ) {
                  const itemBlock = listBlocks[i];
                  i++;

                  let nested = null;
                  if (
                    i < listBlocks.length &&
                    listBlocks[i].depth > currentDepth
                  ) {
                    nested = processLevel(currentDepth + 1);
                  }

                  listItems.push(
                    <li key={itemBlock.key} className="paragraph-text">
                      {itemBlock.styledContent}
                      {nested}
                    </li>,
                  );
                }

                levelElements.push(
                  <ListTag
                    key={`list-${blockAtLevel.key}`}
                    className={clsx(
                      "ps-4 space-xs-mb",
                      styles.list || "list",
                      type === "ordered-list-item"
                        ? "list-decimal"
                        : "list-disc",
                      getAlignmentClass(blockAtLevel),
                    )}
                    style={getAlignmentStyles(blockAtLevel)}
                  >
                    {listItems}
                  </ListTag>,
                );
              }
              return levelElements;
            };

            return processLevel(0);
          };

          elements.push(
            <React.Fragment key={`list-container-${block.key}`}>
              {renderNestedList(currentList)}
            </React.Fragment>,
          );
          currentList = [];
        }
      } else if (block.type === "atomic") {
        const entityKey = block.entityRanges?.[0]?.key;
        const entity = entityMap?.[entityKey];
        if (entity?.type === "IMAGE") {
          elements.push(
            <div
              key={block.key}
              className={clsx(
                "ratio radius-md overflow-hidden bg-cerebral-grey space-xs-mb text-center",
                styles.newsImageWrap,
              )}
            >
              <img
                loading="lazy"
                src={entity.data.src}
                alt={newsItem.name || newsItem.title}
                className="img-fluid object-fit-contain w-100"
              />
            </div>,
          );
        } else if (entity?.type === "TABLE") {
          const rows = entity.data?.rows || [];
          const getCellObj = (cell) => {
            if (typeof cell === "object" && cell !== null) {
              const cellText = cell.text !== undefined && cell.text !== null ? cell.text : "";
              return {
                text: typeof cellText === "object" ? JSON.stringify(cellText) : String(cellText),
                rowSpan: cell.rowSpan || 1,
                colSpan: cell.colSpan || 1,
                bgColor: cell.bgColor || "",
                textColor: cell.textColor || "",
              };
            }
            return {
              text: cell !== undefined && cell !== null ? String(cell) : "",
              rowSpan: 1,
              colSpan: 1,
              bgColor: "",
              textColor: "",
            };
          };

          const formatCssColor = (color) => {
            if (!color) return "";
            const s = String(color).trim();
            if (s === "var(--blue)" || s === "var(--clr-blue)") return "var(--clr-blue, #2e3de5)";
            if (s === "var(--white)" || s === "var(--clr-white)") return "var(--clr-white, #ffffff)";
            if (s === "var(--black)" || s === "var(--clr-black)") return "var(--clr-black, #000000)";
            if (s === "var(--cool-frost)" || s === "var(--clr-off-white)") return "var(--clr-off-white, #f5f5f7)";
            if (s === "var(--sparkling-frost)" || s === "var(--clr-sparkling-frost)") return "var(--clr-sparkling-frost, #d2d4d9)";
            return s;
          };

          const computeGridMatrix = (gridRows) => {
            const numRows = gridRows.length;
            const numCols = gridRows[0]?.length || 0;
            const skip = Array.from({ length: numRows }, () =>
              Array(numCols).fill(false),
            );
            const grid = [];

            for (let r = 0; r < numRows; r++) {
              const row = gridRows[r] || [];
              const renderedCells = [];
              for (let c = 0; c < numCols; c++) {
                if (skip[r][c]) continue;
                const cellObj = getCellObj(row[c]);
                const rSpan = cellObj.rowSpan;
                const cSpan = cellObj.colSpan;

                for (let dr = 0; dr < rSpan; dr++) {
                  for (let dc = 0; dc < cSpan; dc++) {
                    if (dr === 0 && dc === 0) continue;
                    if (r + dr < numRows && c + dc < numCols) {
                      skip[r + dr][c + dc] = true;
                    }
                  }
                }
                renderedCells.push({ rowIndex: r, colIndex: c, cellObj });
              }
              grid.push(renderedCells);
            }
            return grid;
          };

          const grid = computeGridMatrix(rows);

          elements.push(
            <div key={block.key} className="table-responsive space-xs-mb">
              <table className={clsx("w-100 m-0 align-middle", styles.detailTable)}>
                {grid.length > 0 && (
                  <thead>
                    <tr>
                      {grid[0].map(({ colIndex, cellObj }) => {
                        const isBlueHeader =
                          cellObj.bgColor === "var(--blue)" ||
                          cellObj.bgColor === "var(--clr-blue)" ||
                          cellObj.bgColor === "#0000ff" ||
                          cellObj.bgColor === "#2e3de5" ||
                          colIndex === 3 ||
                          cellObj.text.toLowerCase().includes("sustanix");
                        const bgColor = cellObj.bgColor
                          ? formatCssColor(cellObj.bgColor)
                          : isBlueHeader
                          ? "var(--clr-blue, #2e3de5)"
                          : "var(--clr-off-white, #f5f5f7)";
                        const textColor = cellObj.textColor
                          ? formatCssColor(cellObj.textColor)
                          : isBlueHeader
                          ? "var(--clr-white, #ffffff)"
                          : "var(--clr-black, #000000)";

                        const maxCols = Math.max(...grid.map((r) => r.reduce((acc, c) => acc + (c.cellObj.colSpan || 1), 0)));
                        const isLastColumn = colIndex + (cellObj.colSpan || 1) >= maxCols;

                        return (
                          <th
                            key={colIndex}
                            colSpan={cellObj.colSpan}
                            rowSpan={cellObj.rowSpan}
                            className={clsx(
                              "text-sm text-center fw-bold",
                              isLastColumn && styles.lastCol
                            )}
                            style={{
                              backgroundColor: bgColor,
                              color: textColor,
                            }}
                          >
                            {cellObj.text || (
                              <span className="opacity-50 italic">&mdash;</span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {grid.slice(1).map((rowCells, rIdx) => {
                    const rowIndex = rIdx + 1;
                    return (
                      <tr key={rowIndex}>
                        {rowCells.map(({ colIndex, cellObj }) => {
                          const isProductCol =
                            colIndex === 3 ||
                            (rows[0]?.[colIndex] &&
                              getCellObj(rows[0][colIndex])
                                .text.toLowerCase()
                                .includes("sustanix"));
                          const bgColor = cellObj.bgColor
                            ? formatCssColor(cellObj.bgColor)
                            : "var(--clr-white, #ffffff)";
                          const textColor = cellObj.textColor
                            ? formatCssColor(cellObj.textColor)
                            : "var(--clr-black, #000000)";

                          const maxCols = Math.max(...grid.map((r) => r.reduce((acc, c) => acc + (c.cellObj.colSpan || 1), 0)));
                          const isLastColumn = colIndex + (cellObj.colSpan || 1) >= maxCols;

                          return (
                            <td
                              key={colIndex}
                              colSpan={cellObj.colSpan}
                              rowSpan={cellObj.rowSpan}
                              className={clsx(
                                "text-sm text-center",
                                isProductCol && "fw-bold",
                                isLastColumn && styles.lastCol
                              )}
                              style={{
                                backgroundColor: bgColor,
                                color: textColor,
                              }}
                            >
                              {cellObj.text || (
                                <span className="opacity-50 italic">&mdash;</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>,
          );
        }
      } else {
        elements.push(
          <p
            key={block.key}
            className={clsx(
              "paragraph-text space-xs-mb",
              getAlignmentClass(block),
            )}
            style={getAlignmentStyles(block)}
          >
            {styledContent}
          </p>,
        );
      }
    });
    return elements;
  };

  return (
    <>
      <STHeroCommon
        title={"News and Blogs"}
        description={
          "Follow our latest product updates, certifications, partnerships, and milestones as we scale sustainable materials. From research breakthroughs to real-world adoption, these updates reflect how our technology is creating impact."
        }
      />
      <section className="space-xl-py">
        <div className="container">
          <Link
            href={`/news-feed${router.query.tab ? `?tab=${router.query.tab}` : ""}`}
            className={clsx(
              styles.backBtn,
              "d-inline-flex align-items-center gap-2 space-sm-mb",
            )}
          >
            {Icons.arrowLeft}
            <span>Go back</span>
          </Link>
          <div
            className={clsx(
              "ratio radius-md overflow-hidden",
              styles.newsImageWrap,
            )}
          >
            {newsItem.image ? (
              <img
                src={newsItem.image}
                alt={newsItem.name || newsItem.title}
                className="object-fit-contain bg-cerebral-grey w-100 img-fluid"
                fetchpriority="high"
              />
            ) : (
              <div className="bg-cerebral-grey w-100 h-100 d-flex align-items-center justify-content-center text-white">
                <Image src={defaultImg} alt="image placeholder" width={80} />
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between space-md-py flex-wrap gap-xs">
            <div className="d-flex align-items-center gap-5 gap-md-3 flex-wrap">
              <p className="mb-0 text-clr-gray h6 big fw-normal">
                Published by{" "}
                <span className="text-clr-black fw-medium">
                  {typeof newsItem.author === "object" && newsItem.author !== null
                    ? newsItem.author.name || newsItem.author.text || "Sustanix Team"
                    : newsItem.author || "Sustanix Team"}
                </span>
              </p>
              <span
                className={clsx(styles.divider, "d-none d-md-block")}
              ></span>
              <p className="mb-0 text-clr-gray h6 big fw-normal">
                {newsItem.createdAt
                  ? new Date(newsItem.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "Recently"}
              </p>
              <span
                className={clsx(styles.divider, "d-none d-md-block")}
              ></span>
              <p className="mb-0 text-clr-gray h6 big fw-normal">
                {typeof newsItem.tag === "object" && newsItem.tag !== null
                  ? newsItem.tag.text || newsItem.tag.name || "2 mins read"
                  : newsItem.tag || "2 mins read"}
              </p>
            </div>
            <div className="d-flex align-items-center gap-xs">
              <button
                aria-label="Like"
                className={clsx(
                  styles.iconBtn,
                  "d-flex  justify-content-center align-items-center rounded-circle bg-off-white border-0",
                )}
              >
                {Icons.likeIcon}
              </button>
              <button
                aria-label="Comment"
                className={clsx(
                  styles.iconBtn,
                  "d-flex  justify-content-center align-items-center rounded-circle bg-off-white border-0",
                )}
              >
                {Icons.commentIcon}
              </button>
              <STSharePopover
                title="Share Blog"
                shareUrl={
                  newsItem.slug
                    ? `https://sustanix.com/news-feed/${newsItem.slug}/`
                    : ""
                }
              >
                <button
                  aria-label="Share"
                  className={clsx(
                    styles.iconBtn,
                    "d-flex  justify-content-center align-items-center rounded-circle bg-off-white border-0",
                  )}
                >
                  {Icons.shareIcon}
                </button>
              </STSharePopover>
            </div>
          </div>
          {/* <h1 className="primary-title space-sm-mb">{newsItem.title}</h1> */}
          <h1 className="primary-title space-sm-mb">
            {newsItem.name || newsItem.title}
          </h1>

          {/* {newsItem.shot_description && (
            <div className="space-md-mb">
              <p className="paragraph-text mb-0 fw-medium">
                {newsItem.shot_description}
              </p>
            </div>
          )} */}

          <div>
            {descriptionSections
              ? descriptionSections.map((section, index) => (
                  <div key={index} className="space-md-mb">
                    {section.subHeading?.blocks?.map((block) => {
                      return (
                        <h4
                          key={block.key}
                          className={clsx(
                            "space-sm-mb section-title",
                            getAlignmentClass(block),
                          )}
                          style={getAlignmentStyles(block)}
                        >
                          {renderStyledText(
                            block.text,
                            block.inlineStyleRanges,
                            block.entityRanges,
                            section.subHeading?.entityMap,
                          )}
                        </h4>
                      );
                    })}
                    {renderBlocks(
                      section.content?.blocks,
                      section.content?.entityMap,
                    )}
                  </div>
                ))
              : newsItem.long_description && (
                  <div
                    className={clsx(
                      styles.longDescription,
                      "paragraph-text mb-0",
                    )}
                    dangerouslySetInnerHTML={{
                      __html: newsItem.long_description
                        .replace(/<img /gi, '<img loading="lazy" ')
                        .replace(/<iframe /gi, '<iframe loading="lazy" '),
                    }}
                  />
                )}
          </div>

          {/* If the blog was hardcoded before, we keep the sections if there is no long_description */}
          {!newsItem.long_description && (
            <>
              <div className="space-md-mb">
                <h4 className={clsx("space-sm-mb section-title")}>
                  A new chapter: hair based textiles & the Interreg vision
                </h4>
                <p className="paragraph-text mb-0">
                  At Sustanix, we believe in pushing the boundaries of
                  sustainable materials. Today, we’re proud to share that our
                  next journey will take us into uncharted - yet deeply natural
                  - territory: hair-based textiles. This initiative is part of a
                  wider collaborative effort under Interreg, reflecting the core
                  of what Interreg stands for: cross-region cooperation aiming
                  to address shared environmental and societal challenges.
                </p>
              </div>
              {/* ... other hardcoded sections could go here or be removed if we are sure all blogs have long_description ... */}
            </>
          )}
          {/* <div
            className={clsx(styles.dynamicContent, "paragraph-text")}
            dangerouslySetInnerHTML={{ __html: newsItem.long_description }}
          /> */}
        </div>
      </section>
      {/* {newsItem.category === "Blogs" && (
        <div className="bg-off-white space-md-py">
          <div className="container">
            <div className="d-flex align-items-center justify-content-center justify-content-sm-between gap-md flex-wrap flex-sm-nowrap">
              <div className={clsx(styles.logoImgWrap)}>
                <Image src={opzuid} alt="opzuid" className="img-fluid" />
              </div>
              <div className={clsx(styles.logoImgWrap)}>
                <Image
                  src={EuropeanUnion}
                  alt="EuropeanUnion"
                  className="img-fluid"
                />
              </div>
              <div className={clsx(styles.logoImgWrap)}>
                <Image
                  src={ProvinceLimburg}
                  alt="ProvinceLimburg"
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};
